import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { HOTEL } from "@/data/hotel";
import BookingWidget from "@/components/BookingWidget";

const SLIDES = [
  {
    src: HOTEL.images.exterior,
    alt: "Hotel Azulo Inn — boutique hotel exterior in Safdarjung Enclave, New Delhi",
  },
  {
    src: HOTEL.images.img1,
    alt: "Premium Room at Hotel Azulo Inn — king bed, warm lighting",
  },
  {
    src: HOTEL.images.garden,
    alt: "Garden lawn at Hotel Azulo Inn, New Delhi",
    filter: "brightness(1.18) contrast(1.05) saturate(1.05)",
  },
  {
    src: HOTEL.images.img2,
    alt: "Premium Twin Room at Hotel Azulo Inn — king or two single beds",
  },
];

const SLIDE_INTERVAL_MS = 6500;
const CROSSFADE_TRANSITION = {
  opacity: { duration: 1.4, ease: [0.4, 0, 0.2, 1] },
  scale: { duration: 8, ease: "linear" },
};
const HEADLINE_INITIAL = { opacity: 0, y: 18 };
const HEADLINE_ANIMATE = { opacity: 1, y: 0 };
const HEADLINE_TRANSITION = { duration: 1, ease: [0.2, 0.7, 0.1, 1] };

export const Hero = () => {
  const [idx, setIdx] = useState(0);

  // Preload all slides so no black flash during transition
  useEffect(() => {
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const go = (dir) =>
    setIdx((i) => (i + dir + SLIDES.length) % SLIDES.length);

  return (
    <section
      data-testid="hero"
      className="relative w-full min-h-[100svh] overflow-hidden bg-paper-200"
    >
      {/* All slides stacked; only opacity animates — crossfade with no black gap */}
      {SLIDES.map((slide, i) => (
        <motion.img
          key={slide.src}
          src={slide.src}
          alt={slide.alt}
          initial={false}
          animate={{
            opacity: i === idx ? 1 : 0,
            scale: i === idx ? 1 : 1.04,
          }}
          transition={CROSSFADE_TRANSITION}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: slide.filter || "none" }}
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "auto"}
          decoding={i === 0 ? "sync" : "async"}
          data-testid={`hero-slide-${i}`}
        />
      ))}

      {/* Gradient overlays kept light so images stay bright */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900/55 via-ink-900/5 to-transparent" />
      {/* Radial scrim behind the headline area — keeps text legible on every slide without darkening the rest of the image */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 55% at 22% 42%, rgba(20,15,10,0.55) 0%, rgba(20,15,10,0.32) 40%, rgba(20,15,10,0) 72%)",
        }}
      />

      {/* Carousel arrows */}
      <button
        type="button"
        aria-label="Previous photo"
        data-testid="hero-prev"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-paper-50/85 hover:bg-paper-50 text-ink-900 items-center justify-center transition-all"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        aria-label="Next photo"
        data-testid="hero-next"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-paper-50/85 hover:bg-paper-50 text-ink-900 items-center justify-center transition-all"
      >
        <ChevronRight size={20} />
      </button>

      {/* Headline */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-44">
        <motion.div
          initial={HEADLINE_INITIAL}
          animate={HEADLINE_ANIMATE}
          transition={HEADLINE_TRANSITION}
          className="max-w-3xl"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
        >
          <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-paper-50">
            Marwon Group · Since 1980
          </div>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-paper-50">
            Boutique Comfort in
            <span className="italic text-sage-light"> Safdarjung Enclave, New Delhi.</span>
          </h1>
          <p className="mt-6 max-w-xl text-paper-50/95 leading-relaxed text-base md:text-lg">
            A peaceful residential oasis 10 minutes from Chanakyapuri Diplomatic Enclave, US
            Embassy, and AIIMS. Powered by Marwon Hospitality.
          </p>
        </motion.div>
      </div>

      {/* Dots — in flow on mobile, pinned on desktop */}
      <div className="relative flex gap-2 z-10 justify-center mt-10 md:mt-0 md:absolute md:left-1/2 md:-translate-x-1/2 md:bottom-[180px] md:justify-start">
        {SLIDES.map((slide, i) => (
          <button
            key={slide.src}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 transition-all ${i === idx ? "w-8 bg-paper-50" : "w-3 bg-paper-50/40"}`}
          />
        ))}
      </div>

      {/* Booking widget — in flow on mobile, overlaid bottom on desktop */}
      <div className="relative mt-6 px-6 pb-8 md:mt-0 md:px-12 md:pb-10 md:absolute md:inset-x-0 md:bottom-0">
        <div className="max-w-7xl mx-auto">
          <div
            data-testid="direct-booking-badge"
            className="inline-flex items-center gap-2 mb-3 px-4 py-2 rounded-full bg-[#D97706] text-white text-[11px] font-bold tracking-wide uppercase shadow-lg"
          >
            <Star size={13} fill="currentColor" />
            Book Direct &amp; Save 15% vs OTAs + Complimentary Welcome Drink
          </div>
          <BookingWidget variant="hero" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
